<template>
  <SidebarLayout2
    :breadcrumb="breadcrumb"
    pageTitle="Calendário de pontos"
    class="points-page"
  >
    <template #sidebar>
      <!-- NOME DO HOTEL -->
      <SidebarBlock class="points-page-sidebar--hotel">
        <PmwParagraph class="points-page-sidebar--hotel-text">
          {{ name }}
        </PmwParagraph>
      </SidebarBlock>

      <!-- MENU DOS HOTÉIS -->
      <SidebarBlock>
        <PmwHotelNav
          :links="menuLinks"
          :hide="['banners']"
          active="calendario-pontos"
          class="points-page-sidebar--menu"
        />
      </SidebarBlock>

      <SidebarBlock
        v-if="showReturnButton"
        class="points-page-sidebar--actions"
      >
        <PmwParagraph size="large" weight="medium">
          Ações da página
        </PmwParagraph>

        <PmwButton
          link="/hoteis"
          element="router-link"
          layout="outline"
          variation="blue-100"
        >
          Voltar para lista de hotéis
        </PmwButton>
      </SidebarBlock>
    </template>

    <!-- CONTEÚDO PRINCIPAL -->
    <template #content>
      <div class="points-page--content">
        <!-- SUBTITULO -->
        <PmwParagraph
          fontWeight="light"
          fontSize="medium"
          class="points-page--subtitle"
        >
          Os pontos levam em conta as disponibilidades e restrições configuradas
          no tarifário.
          <router-link
            :to="`/hoteis/tarifa-base/${hotelId}`"
            class="text-link points-page--subtitle-link"
          >
            Acesse o calendário para modificar.
          </router-link>
        </PmwParagraph>

        <!-- NOME DO HOTEL -->
        <div class="points-page--hotel">
          <PmwHeading weight="weight-light">
            {{ name }}
          </PmwHeading>
        </div>

        <!-- FILTROS FIXOS -->
        <b-row>
          <!-- QUARTO -->
          <b-col cols="2">
            <FormSelect
              v-model="selectedRoom"
              :options="roomsOptions"
              label="Quartos"
              capitalize
            />
          </b-col>

          <!-- OCUPAÇÃO -->
          <b-col v-if="selectedRoom.value !== 0" cols="2">
            <FormSelect
              v-model="ocupation"
              :options="selectedRoomPaxOptions"
              label="Ocupação"
            />
          </b-col>

          <!-- PERÍODO INICIAL -->
          <b-col cols="2">
            <FormSelect
              v-model="startPeriod"
              :options="startPeriodOptions"
              label="Período inicial"
              capitalize
            />
          </b-col>

          <!-- PERÍODO MOSTRADO -->
          <b-col cols="3">
            <PmwFormField label="Período mostrado">
              <b-row class="mt-3">
                <b-col class="radio-fields">
                  <PmwInputRadio
                    v-model="period"
                    :inputValue="1"
                    :checked="period === 1"
                    label="1 Mês"
                    name="period"
                    class="radio-fields--input"
                  />
                  <PmwInputRadio
                    v-if="showMaxMonths"
                    v-model="period"
                    :inputValue="MaxMonthValue"
                    :checked="period === MaxMonthValue"
                    :label="maxMonthsLabel"
                    name="period"
                    class="radio-fields--input"
                  />
                </b-col>
              </b-row>
            </PmwFormField>
          </b-col>
        </b-row>

        <!-- LINHA DIVISÓRIA -->
        <b-row>
          <b-col>
            <SeparateLine :left="0" :right="0" />
          </b-col>
        </b-row>

        <!-- CALENDÁRIO -->
        <b-row class="mt-4">
          <b-col>
            <CalendarPoints
              :calendarPeriod="calendarPeriod"
              :calendarAttributes="calendarAttributes"
              :loading="loading"
              @showDayEdition="getDayEditionData"
            />
          </b-col>
        </b-row>

        <!-- MODAL EDIÇÃO -->
        <DayEditionModal
          v-model="showEditDayModal"
          :dayData="editionModalCurrentData"
          :hotel="name"
        />
      </div>
    </template>
  </SidebarLayout2>
</template>

<script>
import { addMonths, endOfMonth } from 'date-fns'
import { mapActions, mapGetters } from 'vuex'
import { formatDate } from '@/helpers/date'
import { capitalizeString } from '@/helpers/string'
import SidebarLayout2 from '@/layouts/SidebarLayout2'
import hotelCommonsMixin from '@/mixins/hotelCommonsMixin'
import FormSelect from '@/components/_base/elements/FormSelect'
import PmwFormField from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormField'
import PmwInputRadio from '@solucoes/components-lib-tmp/src/components/elements/PmwInputRadio'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import SeparateLine from '@/components/_base/patterns/SeparateLine'
import DayEditionModal from '@/components/hotel/points/DayEditionModal'
import CalendarPoints from '@/components/hotel/points/CalendarPoints'
import SidebarBlock from '@solucoes/components-lib-tmp/src/components/patterns/SidebarBlock'
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'

export default {
  name: 'PointsCalendar',
  mixins: [hotelCommonsMixin],
  components: {
    SidebarLayout2,
    FormSelect,
    SeparateLine,
    PmwInputRadio,
    PmwFormField,
    PmwParagraph,
    DayEditionModal,
    CalendarPoints,
    PmwHeading,
    SidebarBlock,
    PmwButton
  },
  data() {
    /**
     * Pega a data atual para iniciar a criação das
     * opções do select de período inicial
     * * Data atual + 12 meses
     */
    const startTimeOptions = []
    const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'long' })

    for (let i = 0; i < 12; i++) {
      const date = addMonths(new Date(), i)
      const month = date.getMonth() + 1
      const year = date.getFullYear()
      const index = i

      startTimeOptions.push({
        text: `${formatter.format(date)} ${year}`,
        value: { month, year },
        index: { index }
      })
    }

    return {
      name: '' /** Nome do Hotel */,
      selectedRoom: '' /** Quarto selecionado */,
      rate: '' /** Tarifa selecionada */,
      ocupation: '' /** Quantidade de hóspedes */,
      startPeriod: startTimeOptions[0] /** Mês inicial do calendário */,
      period: 1 /** Quantidade de meses mostrados. 1 ou 6 */,
      rooms: [] /** Quartos do hotel */,
      showEditDayModal: false /** Status de visualização da modal de edição */,
      editionModalCurrentData: {
        title: ''
      },
      /** Caminho de pão */
      breadcrumb: [],
      filterRequestDelay: 1000 /** Delay entre as requisições  do filtro */,
      filterRequest: null /** REquisição do filtro que está para ser executada */,
      startPeriodOptions: startTimeOptions /** Data inicial do período */,
      calendarData: [] /** Dados do calendário de tarifas */,
      loading: true /** Se está carregando os dados do calendeário */,
      hotelMaxMonth: null /** Quantidade de meses máxima do hotel */,
      maxMonthsLabel: null /** Label do período mostrado */,
      showMaxMonths: true /** Controla a visualização do Máx. período mostrado */,
      MaxMonthValue: null /** Controla valor do Máx. período mostrado */
    }
  },
  computed: {
    /** Atributos formatados no padrão do calendário */
    calendarAttributes() {
      return this.calendarData
    },
    /** Período padronizado para o calendário */
    calendarPeriod() {
      const startDate = new Date(
        this.startPeriod.value.year,
        this.startPeriod.value.month - 1,
        1,
        0,
        0,
        0,
        0
      )
      const endDate =
        +this.period > 1
          ? addMonths(startDate, +this.period - 1)
          : endOfMonth(startDate)

      return {
        rawStart: startDate,
        start: formatDate(startDate, 'yyyy-MM-dd'),
        rawEnd: endDate,
        end: formatDate(endDate, 'yyyy-MM-dd')
      }
    },
    /** Se está buscando 1 mês */
    oneMonth() {
      return +this.period === 1
    },
    /** Retorna as opções de quartos ativos no formato do componente */
    roomsOptions() {
      return this.rooms.map((item) => ({
        text: item.nome,
        value: item.codigo
      }))
    },
    /**
     * Retorna as possíveis opções de pax
     * do quarto selecionado
     */
    selectedRoomPaxOptions() {
      const options = []
      const found = this.rooms.find(
        (item) => item.codigo === this.selectedRoom.value
      )

      if (found) {
        /** ADULTOS */
        for (let i = 1; i <= found.maximoAdultos; i++) {
          const obj = {
            text: `${i} ${this.$options.filters.pluralizer(
              i,
              'Adulto | Adultos',
              true
            )}`,
            value: {
              type: 10,
              quantity: i
            }
          }

          options.push(obj)
        }

        /** CRIANÇAS */
        for (let i = 1; i <= found.maximoCriancas; i++) {
          const obj = {
            text: `${i} ${this.$options.filters.pluralizer(
              i,
              'Criança | Crianças',
              true
            )}`,
            value: {
              type: 8,
              quantity: i
            }
          }

          options.push(obj)
        }
      }

      return options
    },

    /** Exibe o botão de voltar para o hotel apenas se o usuário tiver permissão */
    showReturnButton() {
      return this.menuLinks.length > 0
    },

    ...mapGetters('hotel', ['isAbsoluteRate', 'getCurrency'])
  },
  watch: {
    /** Recebe quantidade de meses do hotel para atualizar o select */
    hotelMaxMonth(newValue) {
      const startTimeOptions = []
      const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'long' })

      for (let i = 0; i < newValue + 1; i++) {
        const date = addMonths(new Date(), i)
        const month = date.getMonth() + 1
        const year = date.getFullYear()
        const index = i

        startTimeOptions.push({
          text: `${formatter.format(date)} ${year}`,
          value: { month, year },
          index: { index }
        })
      }

      this.startPeriod = startTimeOptions[0]
      this.startPeriodOptions = startTimeOptions
    },
    selectedRoom(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      /** Seleciona a primeira opção de ocupação do quarto selecionado */
      this.ocupation = this.selectedRoomPaxOptions[0]

      this.getCalendarData()
    },
    rate(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      this.getCalendarData()
    },
    ocupation(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      this.getCalendarData()
    },
    startPeriod(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      /** Salva valor do Index do item selecionado */
      const itemIndex = newValue.index.index

      /** Define o valor max como ValorMax - 1 */
      const customMax = this.hotelMaxMonth

      /** Define mostrar o item como dafault */
      this.showMaxMonths = true

      /** Verifica o valor do index para alterar label e valor do inputRadio */
      if (itemIndex === customMax - 4) {
        this.maxMonthsLabel = '5 Meses'
        this.MaxMonthValue = 5
        this.period = 1
      } else if (itemIndex === customMax - 3) {
        this.maxMonthsLabel = '4 Meses'
        this.MaxMonthValue = 4
        this.period = 1
      } else if (itemIndex === customMax - 2) {
        this.maxMonthsLabel = '3 Meses'
        this.MaxMonthValue = 3
        this.period = 1
      } else if (itemIndex === customMax - 1) {
        this.maxMonthsLabel = '2 Meses'
        this.MaxMonthValue = 2
        this.period = 1
      } else if (customMax === itemIndex) {
        this.showMaxMonths = false
        this.period = 1
      } else {
        this.maxMonthsLabel = '6 Meses'
        this.MaxMonthValue = 6
        this.period = 1
      }

      /** Faz a chamada dos dados */
      this.getCalendarData()
    },
    period(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      this.getCalendarData()
    }
  },
  beforeCreate() {
    /** Salva os meses máximos do hotel */
    this.$calendar.getMaxMonths().then((response) => {
      this.hotelMaxMonth = response.data.numeroMesesCalendario
    })
  },
  async created() {
    /** Define valores de dados iniciais */
    this.maxMonthsLabel = '6 Meses'
    this.MaxMonthValue = 6

    /** Requisita os dados do hotel  */
    await this.requestHotelConfigs(this.$route.params.id).then((response) => {
      this.name = response.nome
      this.breadcrumb = [
        {
          label: 'Letsbook',
          url: '',
          isSpa: false
        },
        {
          label: 'Hotéis',
          url: '/hoteis',
          isSpa: true
        },
        {
          label: capitalizeString(response.nome),
          url: `/hoteis/editar/${response.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: 'Calendário de pontos',
          url: `/hoteis/calendario-pontos/${response.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        }
      ]
    })

    /** Busca as opções de quartos */
    await this.$room.get(this.hotelId).then((response) => {
      this.rooms = response.data.filter((item) => item.ativo)

      /** Seta o primeiro item como o valor inicial */
      try {
        this.selectedRoom = {
          text: capitalizeString(this.rooms[0].nome),
          value: this.rooms[0].codigo
        }
      } catch {
        return null
      }
    })

    this.getCalendarData()
  },
  methods: {
    /**
     * Busca as informações dos quartos do hotel
     * para a data passada
     *
     * @param {Date} date - Data para buscar os dados de allotment
     * @param {Object} dayData - Dados relacionados ao dia selecionado
     */
    getDayEditionData(date, dayData) {
      this.editionModalCurrentData.title = formatDate(date, 'dd MMMM yyyy')
      this.editionModalCurrentData = {
        ...this.editionModalCurrentData,
        ...dayData
      }

      this.showEditDayModal = true
    },
    /**  Busca os dados de allotment para o filtro selecionado */
    getCalendarData() {
      /** Se já existe uma requisição na fila, cancela */
      if (this.filterRequest) {
        clearTimeout(this.filterRequest)
      }

      this.loading = true

      /** Cria uma nova espera para requisitar */
      this.filterRequest = setTimeout(() => {
        this.$calendar
          .getPointsCalendar({
            quarto: +this.selectedRoom.value,
            quantidadePax: +this.ocupation.value.quantity,
            tipoPax: +this.ocupation.value.type,
            mes: +this.startPeriod.value.month,
            ano: +this.startPeriod.value.year,
            numeroMeses: +this.period
          })
          .then((response) => {
            /** Padroniza e guarda os dados para o calendário */
            this.calendarData = response.data.map((item) => {
              const obj = {
                customData: {
                  guest: this.ocupation.text /** Ocupação filtrada */,
                  price: item.qtePontos,
                  showDetail: false,
                  date: item.data /** Data pesquisada */,
                  paxPrices: item.pontosPax /** Preços de todas as ocupações */
                },
                dates: new Date(item.data)
              }

              return obj
            })

            this.loading = false
          })
          .catch((error) => {
            this.$bus.$emit('show-alert-chip', {
              message: error.messages
            })
          })

        clearTimeout(this.filterRequest)
        this.filterRequest = null
      }, this.filterRequestDelay)
    },

    ...mapActions('hotel', ['requestHotelConfigs'])
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .pmw-input-radio input:checked ~ .pmw-input-radio--fake-radio:before {
    background-color: pmw-colors(blue-100);
  }

  .pmw-input-radio input:checked ~ .pmw-input-radio--fake-radio {
    border-color: pmw-colors(blue-100);
  }

  .lb-breadcrumb--link:nth-child(2) {
    cursor: default;
    pointer-events: none;
  }

  .pmw-form-field--label {
    margin-top: -2px;
    font-weight: font-weight(weight-medium);
  }

  .content--white_card .page-heading {
    padding-bottom: 10px;
  }
}
.points-page {
  &--hotel {
    padding-bottom: 25px;
  }

  &--subtitle {
    padding: 0 0 24px 0;
    font-weight: font-weight('weight-light');

    &-link {
      font-weight: font-weight('weight-normal');
    }
  }

  .radio-fields {
    display: flex;

    &--input {
      margin-right: 15px;
    }
  }

  .legend {
    display: flex;
    align-items: center;
    &:before {
      display: inline-block;
      width: 31px;
      height: 2px;
      margin-right: 5px;
      content: '';
      background-color: pmw-colors(success-100);
    }

    &__closed {
      &::before {
        background-color: pmw-colors(error-100);
      }
    }
    &__parcial-closed {
      &::before {
        background-color: pmw-colors(orange-100);
      }
    }
  }

  .circle-legend {
    display: flex;
    align-items: center;
    &:before {
      height: 18px;
      width: 18px;
      border-radius: 50%;
      display: inline-block;
      margin-right: 5px;
      content: '';
      background-color: pmw-colors(success-100);
    }

    &__closed {
      &::before {
        background-color: pmw-colors(error-100);
      }
    }
    &__parcial-closed {
      &::before {
        background-color: pmw-colors(orange-100);
      }
    }
  }

  .col {
    &__self-end {
      display: flex;
      justify-content: flex-end;
    }
  }

  .optional-filters {
    &--icon {
      color: $gray-dark;

      &:hover {
        color: $blue;
      }
    }
  }

  .options-section {
    padding: {
      top: 20px;
      left: 20px;
      right: 20px;
    }

    & + .options-section {
      border-top: 1px solid $gray-border;
    }

    &--option {
      margin-bottom: 20px;
      white-space: nowrap;
    }
  }

  &-sidebar {
    &--hotel {
      background-color: pmw-colors('blue-100');
      height: 85px;

      &-text {
        color: $pmw-white;
        text-align: center;
      }
    }

    &--menu {
      margin-top: -20px;
      max-height: calc(100vh - 280px);
      overflow-y: scroll;
    }

    &--actions {
      padding-top: 10;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
  }
}

::v-deep {
  .content--white_card {
    max-width: min-content;
  }
}
</style>
